/* Zaharkle section finder - Soul Design Project.
 * Direct and simple:
 *  - opens with "What can I help you manifest today?"
 *  - visitor picks a desire (love, money, protection, power, healing, influence)
 *  - visitor picks the KIND of working: a reading, astrology, a product/tool, a full program
 *  - bot confirms the matched COLLECTION (category) - no product picks:
 *      readings -> Readings (astrology chart readings live there too)
 *      programs -> Programs, products/tools -> the desire's collection
 *  - once they confirm the section they want, the 15% discount offer appears:
 *    short lead form (name/email/phone) -> code reveal -> live 15-min timer
 *    -> big "take me there" button opening the collection page (new tab)
 *  - collections only, never individual products. Product pages keep their
 *    own sticky buy bar (handled separately below).
 * No small talk, no interrogation, no discount before they confirm. Fully rule-based (no backend needed).
 * Real Shopify collection links only.
 */
(function () {
  "use strict";

  /* ============================================================
     PURE LOGIC - no DOM. Exported for node tests.
     ============================================================ */
  var LOGIC = {};

  var GREETING = "What can I help you manifest today?";
  var DESIRES = ["Love", "Money", "Protection", "Power", "Healing", "Influence"];
  var DISCOUNT_CODE = "DESIRE15";
  var OFFER_MINUTES = 15;
  var STORE = "https://thesouldesign.myshopify.com";

  /* ---------------- collections: real Shopify collections ---------------- */
  /* key: handle. count = products_count from live collections.json (2026-10-01). */
  var C = {
    "love-lust": { title: "Love & Lust", count: 26,
      desc: "love that actually shows up - attraction, obsession workings, sweetening jars.",
      img: "https://cdn.shopify.com/s/files/1/1013/0132/1860/collections/media-generation-banner-lust-0-2813f6a0-ac70-4429-a821-c408a9b55539.webp?v=1790378100" },
    "money-wealth": { title: "Money & Wealth", count: 31,
      desc: "workings that pull wealth, opportunity, and momentum toward you.",
      img: "https://cdn.shopify.com/s/files/1/1013/0132/1860/collections/media-generation-banner-money-0-b687eaab-0265-472a-be94-dc467ad0e549.webp?v=1790378148" },
    "protection-banishing": { title: "Protection & Banishing", count: 12,
      desc: "shields, banishings, reversals - cut the parasites and walk clean.",
      img: "https://cdn.shopify.com/s/files/1/1013/0132/1860/collections/media-generation-banner-protection-0-036469d5-bd1f-448c-a840-9a1c2af1b6f1.webp?v=1790378217" },
    "power-influence": { title: "Power & Influence", count: 16,
      desc: "presence, authority, influence over rooms, deals, and outcomes.",
      img: "https://cdn.shopify.com/s/files/1/1013/0132/1860/collections/media-generation-banner-power-0-0432d383-6575-4f25-a886-0506ebf81bcf.webp?v=1790378375" },
    "healing-shadow-work": { title: "Healing & Shadow Work", count: 21,
      desc: "healing rituals and shadow work that clear trauma and the patterns keeping you stuck.",
      img: "https://cdn.shopify.com/s/files/1/1013/0132/1860/collections/media-generation-banner-healing-0-d39bdbf5-da47-48c7-b25e-348dc36e993d.webp?v=1790378272" },
    "readings": { title: "Readings", count: 13,
      desc: "tarot, psychic, and divination readings by Isaiah and Alina.",
      img: null },
    "ritual-tools": { title: "Ritual Tools", count: 7,
      desc: "ready-made ritual tools - subliminals, frequencies, and workings.",
      img: "https://cdn.shopify.com/s/files/1/1013/0132/1860/collections/ritual-tools-premade-collection.png?v=1781542641" },
    "done-for-you-rituals-and-spells": { title: "Done-For-You Rituals & Spells", count: 51,
      desc: "rituals and spells performed for you by Isaiah - you bring the intention, we handle the work.",
      img: "https://cdn.shopify.com/s/files/1/1013/0132/1860/collections/rituals-spells-done-for-you.png?v=1781542350" },
    "programs": { title: "Programs", count: 13,
      desc: "full programs that rebuild you from the inside out.",
      img: "https://cdn.shopify.com/s/files/1/1013/0132/1860/collections/programs-collection.png?v=1781543141" },
    "programs-pacts": { title: "Programs & Pacts", count: 14,
      desc: "multi-week transformation systems and real spirit pacts. the deep end.",
      img: null },
    "full-store": { title: "Full Store", count: 219,
      desc: "everything in one place.",
      img: null }
  };

  /* desire -> the collection it lives in */
  var DESIRE_COL = {
    love: "love-lust",
    money: "money-wealth",
    protection: "protection-banishing",
    power: "power-influence",
    healing: "healing-shadow-work",
    influence: "power-influence"
  };

  /* step 2: what KIND of working they want - readings, astrology, products, programs */
  var TYPES = ["A reading", "Astrology", "A product or tool", "A full program"];
  var TYPE_KEYWORDS = {
    reading: ["reading", "readings", "tarot", "divination", "psychic"],
    astrology: ["astro", "astrology", "horoscope", "birth chart", "natal chart", "synastry", "draconic", "zodiac", "planet"],
    product: ["product", "products", "tool", "tools", "spell", "spells", "ritual", "rituals", "buy", "subliminal", "frequency"],
    program: ["program", "programs", "course", "courses", "pact", "pacts", "vip", "coaching", "mentorship"]
  };
  function detectType(text) {
    var t = norm(text), best = null, bestHits = 0;
    Object.keys(TYPE_KEYWORDS).forEach(function (ty) {
      var hits = 0;
      TYPE_KEYWORDS[ty].forEach(function (k) {
        if (wordHit(t, k)) hits++;
      });
      if (hits > bestHits) { bestHits = hits; best = ty; }
    });
    return bestHits > 0 ? best : null;
  }
  /* desire x type -> the best collection. astrology chart readings live in readings. */
  function resolveDestination(desire, type) {
    if (type === "reading") return { handle: "readings", note: "reading" };
    if (type === "astrology") return { handle: "readings", note: "astrology" };
    if (type === "program") return { handle: "programs", note: "program" };
    return { handle: DESIRE_COL[desire] || "full-store", note: null };
  }
  var SOMETHING_ELSE_KEYS = ["something else", "something different", "none of these", "not these", "not quite"];
  function isSomethingElse(text) { return anyHit(norm(text), SOMETHING_ELSE_KEYS); }

  /* the browsable sections offered as chips (full-store shown last as "everything") */
  var SECTION_KEYS = ["love-lust", "money-wealth", "protection-banishing", "power-influence",
    "healing-shadow-work", "readings", "ritual-tools", "done-for-you-rituals-and-spells",
    "programs", "programs-pacts", "full-store"];

  function collectionUrl(handle) { return STORE + "/collections/" + handle; }
  function productUrl(handle) { return STORE + "/products/" + handle; }

  /* typed product search - they name it, we pull it straight up */
  function handleFromUrl(u) {
    var path = String(u || "").split("?")[0];
    var parts = path.split("/");
    return parts[parts.length - 1];
  }
  /* pure: shape suggest.json product hits into cards - testable */
  function hasPrice(p) {
    var n = parseFloat(String(p || "").replace(/[^0-9.]/g, ""));
    return !!(n > 0);
  }
  function parseSuggest(d) {
    var out = [], list = ((((d || {}).resources) || {}).results || {}).products || [], i, p, img;
    for (i = 0; i < list.length && out.length < 5; i++) {
      p = list[i] || {};
      if (!hasPrice(p.price)) continue; /* never card a $0.00 / unknown-price product */
      img = p.featured_image || {};
      out.push({
        title: String(p.title || ""),
        handle: handleFromUrl(p.url),
        price: String(p.price || ""),
        img: String(img.url || "")
      });
    }
    return out;
  }
  function priceStr(p) { return p ? "$" + p : ""; }
  var DISCOUNT_PCT = 15; /* DESIRE15 */
  /* pure: buy-bar totals - testable. cartBase = pre-discount cart subtotal
     (total_price + total_discount so an already-applied DESIRE15 is not double-counted),
     prodCents = price of the variant being added on product pages. */
  function barTotals(cartBaseCents, prodCents) {
    var full = (cartBaseCents || 0) + (prodCents || 0);
    return { full: full, disc: Math.round(full * (100 - DISCOUNT_PCT) / 100) };
  }

  var KEYWORDS = {
    love: ["love", "lover", "relationship", " ex", "ex ", "crush", "romance", "lust", "soulmate", "marriage", "attract", "dating", "heartbreak", "ex back", "get him back", "get her back"],
    money: ["money", "wealth", "rich", "cash", "abundance", "debt", "broke", "income", "business", "sales", "job", "financial", "bills", "paycheck"],
    protection: ["protect", "protection", "protecting", "protected", "curse", "cursed", "hex", "evil eye", "psychic attack", "negative energy", "ward", "shield", "banish", "enemies", "safe", "attack"],
    power: ["power", "dominance", "dominant", "command", "sovereign", "confidence", "control", "authority", "respect", "presence"],
    healing: ["heal", "trauma", "hurt", "pain", "grief", "anxiety", "anxious", "depress", "heartbreak", "inner child", "wound", "ptsd", "stress"],
    influence: ["influence", "charisma", "persuade", "magnetic", "followers", "attention", "charm", "leadership", "reputation"]
  };

  var YES_WORDS = ["yes", "yeah", "yep", "yup", "sure", "ok", "okay", "definitely", "absolutely", "take me there", "lets go", "let s go", "let's go"];
  var ALL_SECTIONS_KEYS = ["show me all sections", "all sections", "other sections", "browse sections", "see everything"];
  var DONE_KEYS = ["i'm good", "im good", "i m good", "no thanks", "all set", "done", "bye", "goodbye"];
  /* Whole-word keyword hit using space padding (no regex, no backslashes:
     norm() pads text with spaces, so " crush " never matches " crushing "). */
  function wordHit(t, k) { return t.indexOf(" " + k.trim() + " ") !== -1; }

  function norm(t) { return " " + String(t).toLowerCase().replace(/[^a-z0-9$ ]/g, " ").replace(/ +/g, " ").trim() + " "; }
  function anyHit(t, keys) {
    for (var i = 0; i < keys.length; i++) { if (t.indexOf(keys[i]) !== -1) return true; }
    return false;
  }
  function isExact(t, w) { return t === " " + w + " "; }

  /* whole-word matching: "crush" must not fire on "crushing debt" */
  function detectDesire(text) {
    var t = norm(text), best = null, bestHits = 0;
    Object.keys(KEYWORDS).forEach(function (d) {
      var hits = 0;
      KEYWORDS[d].forEach(function (k) {
        if (wordHit(t, k)) hits++;
      });
      if (hits > bestHits) { bestHits = hits; best = d; }
    });
    return bestHits > 0 ? best : null;
  }

  /* match typed text to a collection: "love" -> love-lust, exact title words, ordinals */
  function detectCollectionPick(text) {
    var t = norm(text);
    /* direct desire words resolve to their collection */
    var d = detectDesire(text);
    if (d) return DESIRE_COL[d];
    var best = null, bestScore = 0;
    SECTION_KEYS.forEach(function (key) {
      var words = C[key].title.toLowerCase().split(/[^a-z0-9]+/);
      var score = 0;
      words.forEach(function (w) {
        if (w.length > 3 && t.indexOf(" " + w + " ") !== -1) score += 2;
        else if (w.length > 3 && t.indexOf(w) !== -1) score += 1;
      });
      if (score > bestScore) { bestScore = score; best = key; }
    });
    return bestScore > 0 ? best : null;
  }

  function isYes(text) { return YES_WORDS.some(function (w) { return isExact(norm(text), w) || norm(text).indexOf(w) !== -1; }); }
  function wantsAllSections(text) { return anyHit(norm(text), ALL_SECTIONS_KEYS); }
  function isDone(text) { return anyHit(norm(text), DONE_KEYS); }
  function validEmail(s) {
    var e = String(s).trim(), at = e.indexOf("@");
    if (at < 1 || e.indexOf("@", at + 1) !== -1 || e.indexOf(" ") !== -1) return false;
    var dot = e.indexOf(".", at + 1);
    return dot > at + 1 && dot < e.length - 1;
  }
  function validPhone(s) {
    var str = String(s), d = "", i, c;
    for (i = 0; i < str.length; i++) { c = str.charAt(i); if (c >= "0" && c <= "9") d += c; }
    return d.length === 0 || d.length >= 7;
  }

  LOGIC.GREETING = GREETING;
  LOGIC.DESIRES = DESIRES;
  LOGIC.DISCOUNT_CODE = DISCOUNT_CODE;
  LOGIC.OFFER_MINUTES = OFFER_MINUTES;
  LOGIC.C = C;
  LOGIC.DESIRE_COL = DESIRE_COL;
  LOGIC.SECTION_KEYS = SECTION_KEYS;
  LOGIC.collectionUrl = collectionUrl;
  LOGIC.productUrl = productUrl;
  LOGIC.parseSuggest = parseSuggest;
  LOGIC.hasPrice = hasPrice;
  LOGIC.barTotals = barTotals;
  LOGIC.pdpCartKey = pdpCartKey;
  LOGIC.handleFromUrl = handleFromUrl;
  LOGIC.priceStr = priceStr;
  LOGIC.detectDesire = detectDesire;
  LOGIC.detectCollectionPick = detectCollectionPick;
  LOGIC.detectType = detectType;
  LOGIC.resolveDestination = resolveDestination;
  LOGIC.isSomethingElse = isSomethingElse;
  LOGIC.TYPES = TYPES;
  LOGIC.isYes = isYes;
  LOGIC.wantsAllSections = wantsAllSections;
  LOGIC.isDone = isDone;
  LOGIC.validEmail = validEmail;
  LOGIC.validPhone = validPhone;

  /* discount offer: shown at most once per visitor, remembered across pages */
  var SHOWN_KEY = "zd_discount_shown";
  function offerAlreadyShown() {
    try { return window.localStorage.getItem(SHOWN_KEY) === "1"; } catch (e) { return false; }
  }
  function markOfferShown() {
    try { window.localStorage.setItem(SHOWN_KEY, "1"); } catch (e) {}
  }
  LOGIC.offerAlreadyShown = offerAlreadyShown;
  LOGIC.markOfferShown = markOfferShown;

  if (typeof module !== "undefined" && module.exports) { module.exports = LOGIC; }
  if (typeof document === "undefined") return;

  /* ============================================================
     UI STATE + SHELL
     ============================================================ */
  var SESSION_KEY = "zd_chat_seen";
  var OFFER_KEY = "zd_offer_dl";
  var S = null;
  var els = {};

  function freshState() {
    return { step: 0, greeted: false, leadDone: false,
             stage: "desire", desire: null, workingType: null, collection: null, codeRevealed: false,
             productHandle: null, productTitle: "", searching: false };
  }

  function el(tag, cls, html) {
    var d = document.createElement(tag);
    if (cls) d.className = cls;
    if (html != null) d.innerHTML = html;
    return d;
  }
  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  /* ---------------- offer timer (15 min, rolling per session) ---------------- */
  function offerDeadline() {
    var dl = 0, now = Date.now();
    try { dl = parseInt(sessionStorage.getItem(OFFER_KEY) || "0", 10) || 0; } catch (e) {}
    if (!dl || dl <= now) {
      dl = now + OFFER_MINUTES * 60 * 1000;
      try { sessionStorage.setItem(OFFER_KEY, String(dl)); } catch (e) {}
    }
    return dl;
  }
  function fmtLeft(ms) {
    var s = Math.max(0, Math.floor(ms / 1000));
    var m = Math.floor(s / 60), r = s % 60;
    return (m < 10 ? "0" + m : m) + ":" + (r < 10 ? "0" + r : r);
  }
  function tickTimers() {
    var left = offerDeadline() - Date.now();
    var txt = "15% off ends in " + fmtLeft(left);
    var nodes = document.querySelectorAll(".zd-timer");
    for (var i = 0; i < nodes.length; i++) nodes[i].textContent = txt;
  }

  function build() {
    var root = el("div"); root.id = "zd-root";
    root.innerHTML =
      '<div id="zd-overlay" role="dialog" aria-label="Find your ritual">' +
        '<div id="zd-panel">' +
          '<div id="zd-header">' +
            '<div id="zd-avatar">Z</div>' +
            '<div id="zd-title"><b>Zaharkle</b><span><span id="zd-status-dot"></span>find your ritual</span></div>' +
            '<div id="zd-dots"></div>' +
            '<button id="zd-close" aria-label="Close">X</button>' +
          "</div>" +
          '<div id="zd-messages"></div>' +
          '<div id="zd-quick"></div>' +
          '<div id="zd-gobar" style="display:none">' +
            '<div class="zd-bb-info"><b id="zd-go-name"></b>' +
            '<span class="zd-bb-price" id="zd-go-count"></span>' +
            '<span class="zd-timer"></span></div>' +
            '<button id="zd-go-btn">Go</button>' +
          "</div>" +
          '<div id="zd-inputbar">' +
            '<input id="zd-input" type="text" placeholder="what are you after? type it or tap an option..." autocomplete="off" maxlength="500"/>' +
            '<button id="zd-send" aria-label="Send">></button>' +
          "</div>" +
        "</div>" +
      "</div>" +
      '<button id="zd-fab" aria-label="Open chat"><svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg><span class="zd-ping" style="display:none"></span></button>';
    document.body.appendChild(root);
    els.overlay = root.querySelector("#zd-overlay");
    els.panel = root.querySelector("#zd-panel");
    els.messages = root.querySelector("#zd-messages");
    els.quick = root.querySelector("#zd-quick");
    els.input = root.querySelector("#zd-input");
    els.fab = root.querySelector("#zd-fab");
    els.dots = root.querySelector("#zd-dots");
    els.gobar = root.querySelector("#zd-gobar");
    root.querySelector("#zd-close").addEventListener("click", close);
    els.fab.addEventListener("click", function () { open(); if (!S.greeted) { S.greeted = true; askDesire(); } }); /* manual open also greets, once */
    root.querySelector("#zd-go-btn").addEventListener("click", function () {
      var p = els.gobar.getAttribute("data-product");
      var h = els.gobar.getAttribute("data-handle");
      var url = p ? productUrl(p) + "?discount=" + DISCOUNT_CODE : (h ? collectionUrl(h) : null);
      if (url) window.open(url, "_blank", "noopener");
    });
    root.querySelector("#zd-send").addEventListener("click", sendInput);
    els.input.addEventListener("keydown", function (e) { if (e.key === "Enter") sendInput(); });
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && els.overlay.classList.contains("zd-open")) close();
    });
    els.overlay.addEventListener("click", function (e) {
      if (e.target === els.overlay) close();
    });
    setInterval(tickTimers, 1000);
  }

  function open() {
    els.overlay.classList.add("zd-open");
    els.fab.classList.remove("zd-show");
    document.body.style.overflow = "hidden";
    setTimeout(function () { els.input.focus(); }, 350);
  }
  function close() {
    els.overlay.classList.remove("zd-open");
    els.fab.classList.add("zd-show");
    document.body.style.overflow = "";
    try { localStorage.setItem(SESSION_KEY, "1"); } catch (e) {} /* popup auto-opens once per visitor, ever */
  }

  function scrollDown() { els.messages.scrollTop = els.messages.scrollHeight; }
  function addMsg(role, html) {
    var m = el("div", "zd-msg " + (role === "user" ? "zd-user" : "zd-ai"), html);
    els.messages.appendChild(m); scrollDown();
    return m;
  }
  function addText(role, text) { addMsg(role, esc(text)); }
  function typing(on) {
    var t = document.getElementById("zd-typing");
    if (on && !t) {
      t = el("div", "zd-msg zd-ai zd-typing"); t.id = "zd-typing";
      t.innerHTML = "<i></i><i></i><i></i>";
      els.messages.appendChild(t); scrollDown();
    } else if (!on && t) { t.remove(); }
  }
  function setStep(n) {
    S.step = n;
    var html = "";
    for (var i = 1; i <= 3; i++) html += '<i class="' + (i <= n ? "on" : "") + '"></i>';
    els.dots.innerHTML = html;
  }
  /* chips are shortcuts - typing always works too */
  function chips(options, cb) {
    els.quick.innerHTML = "";
    options.forEach(function (opt) {
      var label = typeof opt === "string" ? opt : opt.label;
      var b = el("button", "zd-chip", esc(label));
      b.addEventListener("click", function () { els.quick.innerHTML = ""; cb(opt, label); });
      els.quick.appendChild(b);
    });
    scrollDown();
  }
  /* belt-and-suspenders: no weird symbols ever reach the screen */
  function cleanTitle(t) {
    var s = String(t), out = "", i, code, c, prevSpace = true;
    for (i = 0; i < s.length; i++) {
      code = s.charCodeAt(i);
      c = (code >= 33 && code <= 126) ? s.charAt(i) : " ";
      if (c === " ") { if (!prevSpace) { out += " "; prevSpace = true; } }
      else { out += c; prevSpace = false; }
    }
    if (prevSpace && out.length) out = out.slice(0, -1);
    return out;
  }

  /* one collection card: image, title, one-line desc, count */
  function collectionCard(handle) {
    var c = C[handle]; if (!c) return;
    var imgHtml = c.img
      ? '<img src="' + esc(c.img) + '" alt="" loading="lazy"/>'
      : '<div class="zd-ph">Z</div>';
    var wrap = el("div", "zd-cards");
    var cw = el("div", "zd-cardwrap");
    var box = el("div", "zd-card zd-card-click");
    box.setAttribute("role", "button");
    box.setAttribute("tabindex", "0");
    box.title = "Tap to choose this section";
    box.innerHTML = imgHtml + '<div class="zd-card-body"><b>' + esc(cleanTitle(c.title)) + "</b>" +
      '<span class="zd-why">' + esc(c.desc) + "</span>" +
      '<div class="zd-card-row"><span class="zd-count">' + c.count + ' workings</span>' +
      '<a class="zd-viewlink zd-direct" href="' + esc(collectionUrl(handle)) + '" target="_blank" rel="noopener">view category ></a></div></div>';
    // Tapping the card counts as choosing the section: confirm it and offer the discount.
    // The "view category >" link is the direct path: opens the collection, no discount detour.
    function chooseCard() { offerDiscount(handle); }
    box.addEventListener("click", chooseCard);
    box.addEventListener("keydown", function (e) { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); chooseCard(); } });
    var directLink = box.querySelector(".zd-direct");
    if (directLink) directLink.addEventListener("click", function (e) { e.stopPropagation(); });
    cw.appendChild(box);
    wrap.appendChild(cw);
    els.messages.appendChild(wrap); scrollDown();
  }

  /* typed product search: they type what they're after, we bring that product straight up */
  function searchProducts(q, cb) {
    var url = "/search/suggest.json?q=" + encodeURIComponent(q) +
      "&resources[type]=product&resources[limit]=5&resources[options][unavailable_products]=hide";
    fetch(url, { credentials: "same-origin" })
      .then(function (r) { if (!r.ok) throw new Error("no search"); return r.json(); })
      .then(function (d) { cb(parseSuggest(d)); })
      .catch(function () { cb([]); });
  }
  /* last resort before a nudge: match what they typed against real product names */
  function tryProductSearch(t, done) {
    if (S.searching) { done(true); return; }
    S.searching = true;
    searchProducts(t, function (items) {
      S.searching = false;
      if (items.length) { showSearchResults(items); done(true); }
      else { done(false); }
    });
  }
  function showSearchResults(items) {
    addText("ai", "found it - tap to take a closer look:");
    var wrap = el("div", "zd-cards");
    items.forEach(function (p) {
      if (!p.handle) return;
      var cw = el("div", "zd-cardwrap");
      var box = el("div", "zd-card zd-card-click");
      box.setAttribute("role", "button");
      box.setAttribute("tabindex", "0");
      box.title = "Tap to choose this working";
      var imgHtml = p.img
        ? '<img src="' + esc(p.img) + '" alt="" loading="lazy"/>'
        : '<div class="zd-ph">Z</div>';
      box.innerHTML = imgHtml + '<div class="zd-card-body"><b>' + esc(cleanTitle(p.title)) + "</b>" +
        '<div class="zd-card-row"><span class="zd-price">' + esc(priceStr(p.price)) + '</span>' +
        '<span class="zd-view">view ></span></div></div>';
      function chooseItem() {
        S.productHandle = p.handle;
        S.productTitle = p.title;
        offerDiscount(S.collection || "full-store");
      }
      box.addEventListener("click", chooseItem);
      box.addEventListener("keydown", function (e) { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); chooseItem(); } });
      cw.appendChild(box);
      wrap.appendChild(cw);
    });
    els.messages.appendChild(wrap); scrollDown();
  }

  /* sticky go bar - the confirmed destination, always one tap away */
  function showGoBar(handle) {
    var c = C[handle]; if (!c) return;
    var isProd = !!S.productHandle;
    els.gobar.style.display = "flex";
    els.gobar.setAttribute("data-handle", handle);
    els.gobar.setAttribute("data-product", isProd ? S.productHandle : "");
    document.getElementById("zd-go-name").textContent = isProd ? cleanTitle(S.productTitle || "") : c.title;
    document.getElementById("zd-go-count").textContent = isProd
      ? "15% off with " + DISCOUNT_CODE
      : c.count + " workings - 15% off with " + DISCOUNT_CODE;
    tickTimers();
    scrollDown();
  }

  /* ---------------- lead form: name / email / phone, validated ---------------- */
  function leadForm(handle, done) {
    var c = C[handle];
    var wrap = el("div", "zd-lead");
    wrap.innerHTML =
      '<div class="zd-lead-title">your 15% off is almost ready</div>' +
      '<input id="zd-lf-name" type="text" placeholder="your name" autocomplete="name" maxlength="80"/>' +
      '<input id="zd-lf-email" type="email" placeholder="email" autocomplete="email" maxlength="120"/>' +
      '<input id="zd-lf-phone" type="tel" placeholder="phone (optional)" autocomplete="tel" maxlength="30"/>' +
      '<button id="zd-lf-go">Get my code</button>' +
      '<div class="zd-lead-err" style="display:none"></div>';
    els.messages.appendChild(wrap); scrollDown();
    var name = wrap.querySelector("#zd-lf-name"),
        email = wrap.querySelector("#zd-lf-email"),
        phone = wrap.querySelector("#zd-lf-phone"),
        err = wrap.querySelector(".zd-lead-err");
    function fail(msg) { err.style.display = "block"; err.textContent = msg; scrollDown(); }
    wrap.querySelector("#zd-lf-go").addEventListener("click", function () {
      err.style.display = "none";
      var n = name.value.trim(), e = email.value.trim(), p = phone.value.trim();
      if (!n) { fail("I need your name to hand you the code."); name.focus(); return; }
      if (!validEmail(e)) { fail("that email doesn't look right - try again."); email.focus(); return; }
      if (!validPhone(p)) { fail("that phone number doesn't look right - or leave it blank."); phone.focus(); return; }
      S.leadDone = true;
      emailLeadToStore({ name: n, email: e, phone: p });
      wrap.querySelector("#zd-lf-go").disabled = true;
      done();
    });
  }

  /* ---------------- discount reveal: code + timer + take-me-there ---------------- */
  function revealCode(handle) {
    var c = C[handle]; if (!c) return;
    S.codeRevealed = true;
    offerDeadline(); /* touch the timer so it's fresh */
    var isProd = !!S.productHandle;
    var destUrl = isProd ? productUrl(S.productHandle) + "?discount=" + DISCOUNT_CODE : collectionUrl(handle);
    var destName = isProd ? cleanTitle(S.productTitle || "this working") : cleanTitle(c.title);
    var destKind = isProd ? "opens the working in a new tab" : "opens the section in a new tab";
    seq([
      { t: "your code: " + DISCOUNT_CODE + ". 15% off your whole order - punch it in at checkout.", d: 900 }
    ], function () {
      var wrap = el("div", "zd-cards");
      var cw = el("div", "zd-cardwrap");
      var a = el("a", "zd-card");
      a.href = destUrl; a.target = "_blank"; a.rel = "noopener";
      a.innerHTML = '<div class="zd-ph">Z</div><div class="zd-card-body"><b>' +
        esc("Take me to " + destName) + "</b>" +
        '<span class="zd-why">' + destKind + ' - your code is good for <span class="zd-timer"></span></span></div>';
      cw.appendChild(a);
      wrap.appendChild(cw);
      els.messages.appendChild(wrap); scrollDown();
      tickTimers();
      seq([
        { t: "want me to point you at another section?", d: 900 },
        { chips: ["show me all sections", "I'm good"],
          cb: function (opt, label) { handleUserText(label); } }
      ]);
    });
    showGoBar(handle);
  }

  /* sequential message player: steps are {t} | {chips,cb} | {wait} */
  function seq(steps, done) {
    var i = 0;
    function next() {
      if (i >= steps.length) { if (done) done(); return; }
      var s = steps[i++];
      if (s.wait) { setTimeout(next, s.wait); return; }
      if (s.chips) { chips(s.chips, s.cb); setTimeout(next, 300); return; }
      typing(true);
      setTimeout(function () {
        typing(false); addText("ai", s.t);
        setTimeout(next, 420);
      }, s.d || 850);
    }
    next();
  }
  function say(text, delay, next) { seq([{ t: text, d: delay }], next); }

  /* ============================================================
     FINDER BRAIN - 4 steps: desire > type > confirm section > discount > send
     ============================================================ */
  function askDesire() {
    S.stage = "desire";
    setStep(1);
    seq([
      { t: GREETING, d: 900 },
      { t: "love, money, protection, power, healing, or influence?", d: 700 },
      { chips: DESIRES, cb: function (opt, label) { handleUserText(label); } }
    ]);
  }

  /* step 2: what kind of working - reading, astrology, product/tool, or program */
  function askType() {
    S.stage = "type";
    setStep(2);
    seq([
      { t: "good. what kind of working are you after?", d: 800 },
      { chips: TYPES.concat(["show me all sections"]),
        cb: function (opt, label) { handleUserText(label); } }
    ]);
  }

  /* the section name inside bot messages is a real link - tap it to view the category directly */
  function sectionLink(handle) {
    return '<a class="zd-viewlink" href="' + esc(collectionUrl(handle)) + '" target="_blank" rel="noopener">' +
      esc(C[handle].title) + "</a>";
  }
  /* like say(), but the message body is trusted HTML (for the section link above) */
  function sayHtml(html, delay, next) {
    typing(true);
    setTimeout(function () {
      typing(false); addMsg("ai", html);
      setTimeout(function () { if (next) next(); }, 420);
    }, delay || 850);
  }

  /* step 3: confirm the section they're most interested in */
  function confirmSection(handle, note) {
    var c = C[handle]; if (!c) return;
    S.stage = "confirm";
    S.collection = handle;
    S.productHandle = null;
    S.productTitle = "";
    setStep(2);
    var link = sectionLink(handle);
    var line;
    if (note === "astrology") {
      line = "for astrology, the " + link + " section is where the chart readings live - " +
        "draconic chart, synastry, asteroid goddess. " + c.count + " readings in there. is that the one you want?";
    } else if (note === "reading") {
      line = "the " + link + " section is where all the readings live - " + c.count + " in there. is that the one you want?";
    } else if (note === "program") {
      line = "the " + link + " section is where the full programs live - " + c.count + " in there. is that the one you want?";
    } else {
      line = "so it's " + S.desire + ". the " + link + " section is where all of that lives - " +
        c.count + " workings in there. is that the one you want?";
    }
    sayHtml(line, 900, function () {
      seq([
        { chips: ["yes, take me there", "something else", "show me all sections"],
          cb: function (opt, label) { handleUserText(label); } }
      ], function () {
        collectionCard(handle);
        addText("ai", "or type the name of a specific working and I'll pull it straight up.");
      });
    });
  }

  /* step 3: discount offer -> lead form -> code reveal */
  function offerDiscount(handle) {
    var c = C[handle]; if (!c) return;
    if (offerAlreadyShown()) { revealCode(handle); return; }
    markOfferShown();
    S.stage = "offer";
    S.collection = handle;
    setStep(3);
    var what = S.productHandle ? "that working" : "everything in there";
    seq([
      { t: "before you go in - want 15% off " + what + "? drop your details and I'll hand you the code. it's live for the next 15 minutes.", d: 900 }
    ], function () {
      leadForm(handle, function () { revealCode(handle); });
    });
  }

  function showAllSections() {
    S.stage = "sections";
    var labels = SECTION_KEYS.map(function (k) { return C[k].title; });
    seq([
      { t: "here's every section. which one is calling you?", d: 800 },
      { chips: labels, cb: function (opt, label) { handleUserText(label); } }
    ]);
  }

  /* one short line for common questions, then back to the finder */
  function handleSideIntent(text) {
    var t = norm(text);
    if (anyHit(t, ["thank", "thx", "appreciated", "appreciate it"])) { say("anytime. go get it.", 700); return true; }
    if (anyHit(t, ["bye", "goodbye", "gotta go", "talk later"])) { say("go handle it. tap the chat button anytime.", 700); return true; }
    if (anyHit(t, ["is this real", "is it real", "scam", "legit", "does it work", "actually work", "really work", "proof", "fake"])) {
      say("fair question. ten years of workings, real people, real results - pick what you want and judge by the shift.", 900); return true;
    }
    if (anyHit(t, ["how does", "how do", "how it works", "how this works", "what do i do", "how do i use"])) {
      say("simple: pick the section that matches your desire, find the working that speaks to you, follow the steps that come with it.", 900); return true;
    }
    if (anyHit(t, ["how much", "what's the price", "whats the price", "price?", "cost?", "how expensive", "is it expensive"])) {
      say("most workings run $10 to $97, deeper pacts above that - prices are on the section page.", 800); return true;
    }
    return false;
  }

  function handleUserText(text) {
    var t = String(text).trim();
    if (!t) return;
    addText("user", t);
    els.input.value = "";
    els.quick.innerHTML = "";

    if (S.codeRevealed) {
      /* keep them engaged after the send: another section, or done */
      if (isDone(norm(t))) { say("go get it. tap the chat button anytime you need me.", 700); return; }
      if (wantsAllSections(norm(t))) { showAllSections(); return; }
      var d3 = detectDesire(t);
      if (d3) { S.desire = d3; askType(); return; }
      var c3 = detectCollectionPick(t);
      if (c3) { S.desire = null; confirmSection(c3); return; }
      say('tap a section above, or say "show me all sections".', 700);
      return;
    }

    if (S.stage === "offer") {
      /* they're in the lead form - any text nudges back to the form */
      say("fill in the form above and I'll hand you the code.", 700);
      return;
    }

    if (S.stage === "confirm") {
      if (isYes(t)) { offerDiscount(S.collection); return; }
      if (isSomethingElse(norm(t))) { askType(); return; }
      if (wantsAllSections(norm(t))) { showAllSections(); return; }
      var ty0 = detectType(t);
      if (ty0) { var r0 = resolveDestination(S.desire, ty0); S.workingType = ty0; confirmSection(r0.handle, r0.note); return; }
      var d2 = detectDesire(t);
      if (d2) { S.desire = d2; askType(); return; }
      var c2 = detectCollectionPick(t);
      if (c2) { confirmSection(c2); return; }
      tryProductSearch(t, function (found) {
        if (!found) say("so - is the " + C[S.collection].title + " section the one, or do you want to see all sections?", 700);
      });
      return;
    }

    if (S.stage === "type") {
      if (wantsAllSections(norm(t))) { showAllSections(); return; }
      var ty = detectType(t);
      if (ty) {
        var r = resolveDestination(S.desire, ty);
        S.workingType = ty;
        confirmSection(r.handle, r.note);
        return;
      }
      var dt = detectDesire(t);
      if (dt && dt !== S.desire) { S.desire = dt; askType(); return; }
      say("a reading, astrology, a product or tool, or a full program - which one?", 700);
      return;
    }

    if (S.stage === "sections") {
      var c = detectCollectionPick(t);
      if (c) { confirmSection(c); return; }
      say("tap one of the sections above, or tell me what you're after - love, money, protection, power, healing, or influence.", 700);
      return;
    }

    /* stage "desire" */
    if (handleSideIntent(t)) return;
    var d = detectDesire(t);
    if (d) { S.desire = d; askType(); return; }
    var cp = detectCollectionPick(t);
    if (cp) { confirmSection(cp); return; }
    tryProductSearch(t, function (found) { if (!found) askDesire(); });
  }

  function userSay(text) { handleUserText(text); }
  function sendInput() { userSay(els.input.value); }

  function emailLeadToStore(lead) {
    try {
      var params = new URLSearchParams();
      params.append("form_type", "contact");
      params.append("contact[name]", lead.name);
      params.append("contact[email]", lead.email);
      params.append("contact[body]",
        "Popup chat lead" + NL + "Name: " + lead.name + NL + "Email: " + lead.email +
        NL + "Phone: " + lead.phone + NL + "Desire: " + (S.desire || "unknown") +
        NL + "Type: " + (S.workingType || "unknown") +
        NL + "Section: " + (S.collection ? C[S.collection].title : "unknown") +
        NL + "Item: " + (S.productTitle || "none"));
      fetch("/contact", { method: "POST", body: params, credentials: "same-origin" }).catch(function () {});
    } catch (e) {}
  }

  /* ---------------- sticky buy bar ----------------
     Buy now shows the discounted full cart value and routes through /cart
     (discount auto-applied via the /discount/ link) so the Selleasy
     upsell/downsell offers show before checkout. On product pages with an
     empty cart it shows the discounted product price; once the cart has items
     the bar stays visible on every page. The bar re-renders LIVE whenever the
     cart changes (items added or removed anywhere, including on /cart) instead
     of only once per page load. */
  var NL = String.fromCharCode(10); /* newline without a backslash escape */
  function money(cents) { return "$" + (cents / 100).toFixed(2); }
  /* live cart state: poll the cart and re-render the bar the moment anything
     changes, so removing items updates the button immediately */
  var pdpBarKey = null;   /* last rendered cart state; null = nothing rendered yet */
  var pdpBusy = false;    /* buy click in flight: never re-render under it */
  function pdpCartKey(itemCount, cartBase, handle) {
    return itemCount + ":" + cartBase + ":" + (handle || "");
  }
  function refreshPdpBar() {
    if (pdpBusy) return;
    var path = window.location.pathname, handle = null;
    if (path.indexOf("/products/") === 0) {
      handle = path.slice(10).split("/")[0].split("?")[0].split("#")[0];
    }
    /* cart first: the button shows the discounted full cart value, and the bar
       stays visible on every page once the cart has items in it */
    fetch("/cart.js", { credentials: "same-origin" })
      .then(function (r) { if (!r.ok) throw new Error("no cart"); return r.json(); })
      .then(function (cart) {
        var itemCount = cart.item_count || 0;
        var cartBase = (cart.total_price || 0) + (cart.total_discount || 0);
        var key = pdpCartKey(itemCount, cartBase, handle);
        if (key === pdpBarKey) return; /* nothing changed since last render */
        pdpBarKey = key;
        if (!handle) {
          if (itemCount === 0) { removePdpBar(); return; } /* cart emptied: hide */
          renderPdpBar("Your cart (" + itemCount + ")", 0, cartBase, null);
          return;
        }
        fetch("/products/" + handle + ".js", { credentials: "same-origin" })
          .then(function (r) { if (!r.ok) throw new Error("no product"); return r.json(); })
          .then(function (prod) {
            if (key !== pdpBarKey) return; /* cart changed while fetching: drop stale */
            var variants = prod.variants || [];
            var v = variants.filter(function (x) { return x.available; })[0] || variants[0];
            if (!v) { removePdpBar(); return; }
            renderPdpBar(prod.title, v.price, cartBase, { id: v.id, handle: handle });
          })
          .catch(function () {});
      })
      .catch(function () {});
  }
  function removePdpBar() {
    var old = document.getElementById("zd-pdpbar");
    if (old && old.parentNode) old.parentNode.removeChild(old);
    var root = document.getElementById("zd-root");
    if (root) root.classList.remove("zd-pdp");
  }
  function renderPdpBar(name, prodCents, cartBase, variant) {
    offerDeadline(); /* start the shared 15-min session timer */
    removePdpBar(); /* drop any stale bar before building the fresh one */
    var t = barTotals(cartBase, prodCents);
    var bar = el("div"); bar.id = "zd-pdpbar";
    bar.innerHTML =
      '<div class="zd-pdp-info"><span class="zd-pdp-name">' + esc(name) + "</span>" +
      '<span class="zd-pdp-price">' + money(t.disc) + " <s>" + money(t.full) + "</s></span>" +
      '<span class="zd-timer"></span></div>' +
      '<button id="zd-pdp-go">Buy now - ' + money(t.disc) +
      "<small>" + DISCOUNT_PCT + "% off auto-applied</small></button>";
    var root = document.getElementById("zd-root");
    root.appendChild(bar);
    root.classList.add("zd-pdp");
    tickTimers();
    bar.querySelector("#zd-pdp-go").addEventListener("click", function () {
      var btn = this; btn.disabled = true; pdpBusy = true;
      var goCart = function () {
        /* through the cart page (not straight to checkout) so the store's
           Selleasy upsell/downsell offers get their turn; the discount link
           applies DESIRE15 to the session, then lands on /cart */
        window.location.href = "/discount/" + encodeURIComponent(DISCOUNT_CODE) + "?redirect=/cart";
      };
      if (!variant) { goCart(); return; } /* cart already has items: just discount + show cart */
      fetch("/cart/add.js", {
        method: "POST", credentials: "same-origin",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ id: variant.id, quantity: 1 })
      }).then(function (r) { if (!r.ok) throw new Error("add failed"); })
        .then(goCart)
        .catch(function () { pdpBusy = false; btn.disabled = false; window.location.href = "/products/" + variant.handle; });
    });
  }

  /* ---------------- boot ---------------- */
  function start() {
    S = freshState();
    build();
    refreshPdpBar();
    setInterval(refreshPdpBar, 3000); /* live cart updates: button refreshes the moment items are added or removed */
    var isHome = window.location.pathname === "/" || window.location.pathname === "";
    var seen = false;
    try { seen = !!localStorage.getItem(SESSION_KEY); } catch (e) {}
    if (isHome && !seen) {
      setTimeout(function () {
        open();
        if (!S.greeted) {
          S.greeted = true;
          askDesire();
        }
      }, 2500);
    } else {
      els.fab.classList.add("zd-show");
    }
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start);
  else start();

  /* test hook (browser section only) */
  LOGIC._test = {
    start: start,
    handleUserText: handleUserText,
    getState: function () { return S; },
    messageCount: function () { return els.messages ? els.messages.children.length : -1; }
  };
})();
